import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import "./CustomModal.css";

/**
 * CustomModal — the app's own dialog.
 *
 *   <CustomModal
 *     open={isOpen}
 *     size="small"                 // "small" | "medium" (default) | "large"
 *     title="Custom"
 *     className="my-dialog"        // lands on the panel, for page-level restyling
 *     footer={<…buttons…/>}
 *     onClose={…}
 *   >
 *     …body…
 *   </CustomModal>
 *
 * Defaults: 12px corners, no border, medium width. Sizes are maxima and the
 * panel is fluid under them, so it fits a narrow window instead of forcing the
 * page sideways; below 560px every size is full width.
 *
 * Class names are all prefixed `cmdl-`, which nothing else in the app uses, so
 * this neither overrides nor is overridden by antd's `.ant-modal-*` or any
 * page's own rules.
 *
 * Width is the one default a caller's class may not beat at equal specificity
 * — pass `width` for that.
 */

const SIZES = ["small", "medium", "large"];

const CloseIcon = (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export default function CustomModal({
  open,
  onClose,
  title,
  footer,
  children,
  size = "medium",
  width,
  className = "",
  bodyClassName = "",
  closable = true,
  maskClosable = true,
  keyboard = true,
  /* Drops the body's own padding, for a dialog that wants to paint edge to
     edge — a media preview, a full-bleed list. */
  bare = false,
}) {
  /* Mount outlives `open` by the length of the transition so the dialog can
     fade out instead of vanishing; `entered` is set a frame after mount so the
     transform has a starting point to move from. */
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() => setEntered(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setEntered(false);
    const timer = setTimeout(() => setMounted(false), 210);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open || !keyboard) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, keyboard, onClose]);

  // The page behind must not scroll away under the mask.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Unmounted once the fade-out has finished, so the children are gone —
  // a dialog that re-opens starts fresh rather than showing the last state.
  if (!mounted) return null;

  const sizeClass = SIZES.indexOf(size) !== -1 ? size : "medium";
  const hasHeader = Boolean(title) || closable;

  /* Only a press that both starts and ends on the mask counts. Without this, a
     drag that begins inside the dialog and releases outside it would close the
     dialog mid-selection. */
  const handleOverlayMouseDown = (e) => {
    if (!maskClosable) return;
    if (e.target !== e.currentTarget) return;
    e.currentTarget.dataset.pressedMask = "true";
  };

  const handleOverlayClick = (e) => {
    if (!maskClosable) return;
    const pressedMask = e.currentTarget.dataset.pressedMask === "true";
    delete e.currentTarget.dataset.pressedMask;
    if (pressedMask && e.target === e.currentTarget) onClose?.();
  };

  return ReactDOM.createPortal(
    <div className={`cmdl-root ${entered ? "is-open" : ""}`}>
      <div className="cmdl-overlay" onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
        <div
          className={["cmdl-panel", `cmdl-panel--${sizeClass}`, className].filter(Boolean).join(" ")}
          style={width ? { maxWidth: width } : undefined}
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === "string" ? title : undefined}
        >
          {hasHeader && (
            <div className={`cmdl-head ${title ? "" : "cmdl-head--bare"}`}>
              {title ? <div className="cmdl-title">{title}</div> : null}
              {closable && (
                <button type="button" className="cmdl-close" onClick={onClose} aria-label="Close">
                  {CloseIcon}
                </button>
              )}
            </div>
          )}

          <div
            className={["cmdl-body", bodyClassName].filter(Boolean).join(" ")}
            style={bare ? { padding: 0 } : undefined}
          >
            {children}
          </div>

          {footer ? <div className="cmdl-foot">{footer}</div> : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
